import { CandleType } from 'klinecharts'
import { CATEGORIES, listListedByCategory } from '@/indicators'
import type { Category, IndicatorDef } from '@/indicators'
import type { ChartTypeId, VolumeOption } from './types'

/** Timeframe options (a curated subset of the datafeed's supported TIMEFRAMES). */
export const TIMEFRAME_OPTIONS = ['M1', 'M5', 'M15', 'M30', 'H1', 'H4', 'D1', 'W1'] as const

/** TradingView-style display labels for the timeframe dropdown. */
export const TIMEFRAME_LABELS: Record<(typeof TIMEFRAME_OPTIONS)[number], string> = {
  M1: '1m',
  M5: '5m',
  M15: '15m',
  M30: '30m',
  H1: '1h',
  H4: '4h',
  D1: '1D',
  W1: '1W',
}

/**
 * The four chart shapes the picker offers. `engine` is the klinecharts candle
 * type; Heikin Ashi has no native type, so it renders as solid candles over an
 * HA-transformed series (see heikinAshi.ts).
 */
export interface ChartTypeDef {
  id: ChartTypeId
  label: string
  engine: CandleType
  ha: boolean
}

export const CHART_TYPES: ChartTypeDef[] = [
  { id: 'bar', label: 'Bars', engine: CandleType.Ohlc, ha: false },
  { id: 'candle', label: 'Candles', engine: CandleType.CandleSolid, ha: false },
  { id: 'heikin', label: 'Heikin Ashi', engine: CandleType.CandleSolid, ha: true },
  { id: 'line', label: 'Line', engine: CandleType.Area, ha: false },
]

export const chartTypeById = (id: string): ChartTypeDef =>
  CHART_TYPES.find((t) => t.id === id) ?? CHART_TYPES[1]

/** The special Volume entry — intercepted by the workbench, not a normal instance. */
export const VOLUME_OPTION: VolumeOption = { key: 'VOLUME', label: 'Volume' }

/** One row of the indicator picker: a listed def, or the special Volume entry. */
export type PickerEntry = { kind: 'def'; def: IndicatorDef } | { kind: 'volume' }

/** One picker section: a category header and its rows. */
export interface PickerGroup {
  category: Category
  label: string
  entries: PickerEntry[]
}

/**
 * The picker's sections, in CATEGORIES order: the latest listed version of
 * every indicator under its category, with the Volume overlay opening the
 * Volume section. Older versions and dropped ids never appear here;
 * `resolveDef` still knows them, so saved instances keep working.
 */
export function pickerGroups(): PickerGroup[] {
  const byCategory = new Map(listListedByCategory().map((g) => [g.category, g.defs]))
  return CATEGORIES.map((c) => {
    const entries: PickerEntry[] = (byCategory.get(c.id) ?? []).map((def) => ({ kind: 'def', def }))
    if (c.id === 'volume') entries.unshift({ kind: 'volume' })
    return { category: c.id, label: c.label, entries }
  }).filter((g) => g.entries.length > 0)
}
